import axios from "axios";
import { FC, useState, useEffect } from "react";
import "./App.css";
import { SpinnerCircularFixed, SpinnerRound } from "spinners-react";
import { CheckboxField } from "./CheckboxField";
import logo from "./icons/chainvalley-logo.svg";
import wirexLogo from "./icons/wirex-logo.svg";

const Api = axios.create({
  baseURL: process.env.REACT_APP_CRC_API_URL + "/public",
});

interface IToSFormProps {
  linkToken: string;
  onSubmitSuccess(): void;
}

export const ToSForm: FC<IToSFormProps> = ({ onSubmitSuccess, linkToken }) => {
  const [isTosChecking, setIsTosChecking] = useState(true);
  const [linkTokenError, setLinkTokenError] = useState<
    "invalid" | "expired" | undefined
  >();
  const [notRequestedError, setNotRequestedError] = useState(false);
  const [unknownError, setUnknownError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isTosAccepted, setIsTosAccepted] = useState(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const [isKycSharingAccepted, setIsKycSharingAccepted] = useState(false);
  const [isBalanceTransferAccepted, setIsBalanceTransferAccepted] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    setUnknownError(false);
    setLinkTokenError(undefined);
    setNotRequestedError(false);
    try {
      const res = await Api.post("/tos/cv/accept", { linkToken, isKycSharingAccepted: true, isBalanceTransferAccepted: true });
      if (res.data.data.redirectUrl) {
        window.location.assign(res.data.data.redirectUrl);
        return;
      }
      onSubmitSuccess();
    } catch (e: any) {
      setError(e);
    }
  };

  useEffect(() => {
    const fetchTosStatus = async () => {
      try {
        const { data: { data } } = await Api.post("/tos/cv/check", { linkToken });
        if (data.accepted && data.isKycSharingAccepted && data.isBalanceTransferAccepted) {
          if (data.isKycPassed) {
            onSubmitSuccess();
          } else {
            await handleSubmit();
          }
        } else {
          setIsTosAccepted(data.accepted);
        }
      } catch {}
      finally {
        setIsTosChecking(false);
      }
    }
    fetchTosStatus();
  }, []);

  const setError = (e: any) => {
    const error = e.response?.data?.error?.error;
    if (
      error === "CV_TOS_ALREADY_ACCEPTED" ||
      error === "TOS_ALREADY_ACCEPTED"
    ) {
      setNotRequestedError(true);
    } else if (
      error === "CV_TOS_LINK_EXPIRED" ||
      error === "LINK_TOKEN_EXPIRED"
    ) {
      setLinkTokenError("expired");
    } else if (
      error === "CV_TOS_NOT_REQUESTED" ||
      error === "INVALID_LINK_TOKEN"
    ) {
      setLinkTokenError("invalid");
    } else {
      setUnknownError(true);
    }
    setLoading(false);
  };

  const hasError = Boolean(unknownError || notRequestedError || linkTokenError);

  const handleCheck = () => {
    const newValue = !checkboxChecked;
    setCheckboxChecked(newValue);
    setUnknownError(false);
    if (!newValue) {
      setIsKycSharingAccepted(false);
      setIsBalanceTransferAccepted(false);
    }
  };

  const handleKycSharingCheck = () => {
    const newValue = !isKycSharingAccepted;
    setIsKycSharingAccepted(newValue);
    if (newValue) {
      setCheckboxChecked(true);
    } else {
      setIsBalanceTransferAccepted(false);
    }
  }

  const handleBalanceTransferCheck = () => {
    const newValue = !isBalanceTransferAccepted;
    setIsBalanceTransferAccepted(newValue);
    if (newValue) {
      setCheckboxChecked(true);
      setIsKycSharingAccepted(true);
    }
  }

  if (isTosChecking) {
    return (
      <div className="loader">
        <SpinnerRound
          size={64}
          color="#cfcfcf"
        />
      </div>
    )
  }

  return (
    <>
      <div className="container">
        <div className="logo logo-wide">
          <img src={logo} alt="Chain Valley" />
        </div>

        <div className="section">
          <h1>
            Next Generation Card Platform
          </h1>
          <h3>
            We are migrating card services to a new infrastructure to improve service stability, increase transaction speed, and enable higher limits.
          </h3>
          <div className="hr" />
          <p>
            The new card infrastructure is powered by <strong>Wirex</strong>, with technical integration and service management provided by <strong>Chain Valley Sp. z o.o.</strong>
          </p>
          <p>
            To continue using card services, <strong>your profile data must be migrated from Utrg UAB to Chain Valley Sp. z o.o.</strong>, which will act as the service provider for the card product going forward.
          </p>
          <p>
            Your personal data will be processed <strong>solely</strong> for the purposes of card service provision, regulatory compliance, fraud prevention, and technical setup, in accordance with applicable data protection laws.
          </p>
          <p>
            Please review the information below and confirm the required consents to proceed.
          </p>
        </div>
        <div className="section">
          <div className="checkbox-section">
            {!isTosAccepted && (
              <CheckboxField
                checked={checkboxChecked}
                onChange={handleCheck}
                label={<>I confirm that I have read and accept the <a href="https://chainvalley.pro/tou" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Terms of Use</a> and <a href="https://chainvalley.pro/pp" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline' }}>Privacy Policy</a> of Chain Valley Sp. z o.o.</>}
              />
            )}
            <CheckboxField
              checked={isKycSharingAccepted}
              onChange={handleKycSharingCheck}
              label="I request the transfer of my personal data from Utrg UAB to Chain Valley Sp. z o.o. for card services and legal compliance."
            />
            <CheckboxField
              checked={isBalanceTransferAccepted}
              onChange={handleBalanceTransferCheck}
              label="I instruct the transfer of my EUR balance to my new card account within the Wirex infrastructure."
            />
          </div>
          {hasError && (
            <div className="field-error footer">
              {notRequestedError && "Terms of Use already accepted."}
              {Boolean(linkTokenError) && (
                <>
                  {linkTokenError === "expired"
                    ? "Your link has been expired. "
                    : "Your link is not valid. "}
                  <a href="/">Request new link</a>
                </>
              )}
              {unknownError && "Something went wrong. Please try again later"}
            </div>
          )}
          <p>
            <i>
              If you do not confirm the above, we will be unable to continue providing card services. You may request account closure and balance withdrawal in accordance with applicable terms and law.
            </i>
          </p>
          {!loading && (
            <button
              type="button"
              className="button"
              disabled={Boolean(
                (!checkboxChecked && !isTosAccepted) || !isBalanceTransferAccepted || !isKycSharingAccepted || linkTokenError || notRequestedError
              )}
              onClick={handleSubmit}
            >
              Next
            </button>
          )}
          {loading && (
            <div className="button loading">
              <SpinnerCircularFixed
                size={24}
                color="#ffffff"
                secondaryColor="#0C0D0F"
              />
            </div>
          )}
        </div>
      </div>
      <div className="copyright">
        Chain Valley Sp. z o.o. &copy; All Rights Reserved.
      </div>
    </>
  );
};
